// Cliente del geocodificador CartoCiudad
// Servicio público de geocodificación y reverse geocoding
// Sin clave, CORS accesible
import type { MultiPoligono } from '../geo/geometria';
import { parsearWktMultiPoligono } from '../geo/wkt';

export interface LugarCartoCiudad {
  nombre: string;
  tipo: string; // 'callejero', 'portal', 'vía', etc.
  lon: number;
  lat: number;
  municipio?: string;
  provincia?: string;
  extent?: [number, number, number, number]; // bbox
}

export interface DireccionCartoCiudad {
  direccion: string;
  municipio?: string;
  provincia?: string;
}

export interface RespuestaCartoCiudadFind {
  id: string;
  type: string;
  address?: string;
  postalCode?: string;
  muni?: string;
  province?: string;
  lat?: number;
  lng?: number;
  geom?: string;
}

export interface RespuestaCartoCiudadReverse {
  id: string;
  type: string;
  address?: string;
  muni?: string;
  province?: string;
  lat?: number;
  lng?: number;
  refCatastral?: string;
}

export type CoordenadaCoordenadas = { lon: number; lat: number };
export type CoordenadaUtm = { x: number; y: number; huso: number };
export type CoordenadaAny = CoordenadaCoordenadas | CoordenadaUtm;

/**
 * Busca un lugar por nombre o dirección usando CartoCiudad.
 * @param texto Nombre, dirección o lugar a buscar
 * @param f Función fetch inyectable para tests
 * @returns Objeto con información del lugar, o null si no encuentra
 */
export async function buscarLugar(
  texto: string,
  f: typeof fetch = fetch
): Promise<LugarCartoCiudad | null> {
  const url = new URL("https://www.cartociudad.es/geocoder/api/geocoder/find");
  url.searchParams.set("q", texto);

  try {
    const resp = await f(url.toString());
    if (!resp.ok) {
      return null;
    }
    const data = (await resp.json()) as RespuestaCartoCiudadFind;

    if (!data.lat || !data.lng) {
      return null;
    }

    return {
      nombre: data.address || "Sin nombre",
      tipo: data.type || "desconocido",
      lon: data.lng,
      lat: data.lat,
      municipio: data.muni,
      provincia: data.province,
    };
  } catch (err) {
    throw new Error(`Error buscando lugar en CartoCiudad: ${String(err)}`);
  }
}

/**
 * Obtiene la dirección de un punto usando reverse geocoding de CartoCiudad.
 * @param lon Longitud en WGS84
 * @param lat Latitud en WGS84
 * @param f Función fetch inyectable para tests
 * @returns Objeto con dirección y localidad, o null si error
 */
export async function direccionDesdePunto(
  lon: number,
  lat: number,
  f: typeof fetch = fetch
): Promise<DireccionCartoCiudad | null> {
  const url = new URL("https://www.cartociudad.es/geocoder/api/geocoder/reverseGeocode");
  url.searchParams.set("lon", String(lon));
  url.searchParams.set("lat", String(lat));

  try {
    const resp = await f(url.toString());
    if (!resp.ok) {
      return null;
    }
    const data = (await resp.json()) as RespuestaCartoCiudadReverse;

    if (!data.address) {
      return null;
    }

    return {
      direccion: data.address,
      municipio: data.muni,
      provincia: data.province,
    };
  } catch (err) {
    throw new Error(`Error en reverse geocoding CartoCiudad: ${String(err)}`);
  }
}

/**
 * Valida y normaliza una coordenada geográfica o UTM.
 * Acepta:
 *   - "40.4168, -3.7038" (lat, lon en grados decimales)
 *   - "440300, 4474300 30" (x y huso en UTM)
 *   - "440300 4474300" (x y en UTM, huso 30 por defecto)
 * @param texto Texto que contiene una coordenada
 * @returns Objeto tipado con lon/lat (WGS84) o x/y/huso (UTM), o null si inválido
 */
export function esCoordenada(texto: string): CoordenadaAny | null {
  // Dos o tres numeros separados por coma, punto y coma o espacios.
  const m = texto.trim().match(/^([+-]?\d+(?:\.\d+)?)\s*[,;\s]\s*([+-]?\d+(?:\.\d+)?)(?:\s*[,;\s]\s*(\d{1,2}))?$/);
  if (!m) return null;
  const a = parseFloat(m[1]);
  const b = parseFloat(m[2]);
  const husoTexto = m[3];

  // UTM: easting de 6 cifras y northing de 7 cifras (Espana: 100.000-900.000 / 3.000.000-4.900.000).
  if (a >= 100_000 && a < 1_000_000 && b >= 3_000_000 && b < 10_000_000) {
    let huso = husoTexto ? parseInt(husoTexto, 10) : 30;
    if (huso < 1 || huso > 60) huso = 30;
    return { x: a, y: b, huso };
  }

  // Geograficas: "lat, lon" en grados decimales (sin huso).
  if (!husoTexto && a >= -90 && a <= 90 && b >= -180 && b <= 180) {
    return { lat: a, lon: b };
  }
  return null;
}

// ---------- Municipios (limite municipal para cargar y filtrar por termino) ----------

export interface MunicipioCandidato {
  /** Codigo INE de cinco cifras (provincia + municipio). */
  codigo: string;
  nombre: string;
  provincia: string | null;
}

export interface Municipio extends MunicipioCandidato {
  /** Limite municipal en WGS84 (lon, lat), tal como lo sirve CartoCiudad. */
  poligono: MultiPoligono;
  lon: number;
  lat: number;
}

interface CandidatoCrudo {
  id?: string;
  type?: string;
  muni?: string;
  muniCode?: string;
  province?: string;
  address?: string;
}

/**
 * Municipios cuyo nombre encaja con el texto. `candidates` devuelve tambien poblaciones y
 * calles; se filtra por type=Municipio (su id es el codigo INE). Comprobado el 2026-09-23:
 * "Cortes de la Frontera" -> {codigo 29046, Malaga}.
 */
export async function buscarMunicipios(texto: string, f: typeof fetch = fetch): Promise<MunicipioCandidato[]> {
  const url = new URL('https://www.cartociudad.es/geocoder/api/geocoder/candidates');
  url.searchParams.set('q', texto);
  url.searchParams.set('limit', '15');
  const resp = await f(url.toString());
  if (!resp.ok) throw new Error(`CartoCiudad respondió HTTP ${resp.status}`);
  const datos = (await resp.json()) as CandidatoCrudo[];
  const vistos = new Set<string>();
  const salida: MunicipioCandidato[] = [];
  for (const d of Array.isArray(datos) ? datos : []) {
    if ((d.type ?? '').toLowerCase() !== 'municipio') continue;
    const codigo = d.muniCode ?? d.id;
    if (!codigo || vistos.has(codigo)) continue;
    vistos.add(codigo);
    salida.push({ codigo, nombre: d.muni ?? d.address ?? codigo, provincia: d.province ?? null });
  }
  return salida;
}

/**
 * Limite municipal por codigo INE. `find?q=` con type=Municipio devuelve el poligono de la
 * poblacion; hay que pedirlo por `id` para obtener el termino completo (comprobado 2026-09-23).
 */
export async function poligonoMunicipio(codigo: string, f: typeof fetch = fetch): Promise<Municipio> {
  const url = new URL('https://www.cartociudad.es/geocoder/api/geocoder/find');
  url.searchParams.set('id', codigo);
  url.searchParams.set('type', 'Municipio');
  const resp = await f(url.toString());
  if (!resp.ok) throw new Error(`CartoCiudad respondió HTTP ${resp.status}`);
  const d = (await resp.json()) as RespuestaCartoCiudadFind & { muniCode?: string; muni?: string; province?: string };
  if (!d || !d.geom) throw new Error('CartoCiudad no devolvió el límite del municipio.');
  return {
    codigo: d.muniCode ?? codigo, nombre: d.muni ?? d.address ?? codigo, provincia: d.province ?? null,
    poligono: parsearWktMultiPoligono(d.geom), lon: d.lng ?? 0, lat: d.lat ?? 0,
  };
}
